import { memo } from 'react';
import { ArrowRight, TrendingUp, Clock, Target } from 'lucide-react';
import { Tooltip, KPI_TOOLTIPS } from '@/components/ui';
import type { LeadToDealConversion, ConvertedLead } from '@/utils/analytics';
import type { Deal } from '@/types';
import type { AnalyticsDetailData } from './AnalyticsDetailModal';

interface LeadToDealTrackerProps {
  data: LeadToDealConversion;
  deals?: Deal[];
  isLoading?: boolean;
  onMetricClick?: (data: AnalyticsDetailData) => void;
}

export const LeadToDealTracker = memo(function LeadToDealTracker({ data, deals = [], isLoading, onMetricClick }: LeadToDealTrackerProps) {
  // Sort converted leads by most recent
  const recentConversions = [...data.convertedLeads]
    .sort((a, b) => new Date(b.deal.createdAt).getTime() - new Date(a.deal.createdAt).getTime())
    .slice(0, 5);

  const handleConvertedClick = () => {
    if (!onMetricClick) return;
    // Get deals that were converted from leads
    const convertedDeals = data.convertedLeads.map((c) => c.deal);
    onMetricClick({
      title: 'Converted Leads → Deals',
      subtitle: `${data.convertedCount} leads converted to deals`,
      deals: convertedDeals,
    });
  };

  const handleTotalDealsClick = () => {
    if (!onMetricClick) return;
    onMetricClick({
      title: 'All Deals',
      subtitle: `${data.totalDeals} total deals in pipeline`,
      deals: deals,
    });
  };

  return (
    <div className="rounded-xl border border-zinc-800 bg-zinc-900/50 p-6">
      <div className="flex items-center gap-2 mb-4">
        <Target className="h-5 w-5 text-emerald-400" />
        <Tooltip {...KPI_TOOLTIPS.conversionRate}>
          <h3 className="text-lg font-semibold text-white">Lead → Deal Conversion</h3>
        </Tooltip>
      </div>
      <p className="text-xs text-zinc-500 mb-4">Track leads that converted to deals</p>

      {isLoading ? (
        <div className="flex items-center justify-center py-8">
          <div className="h-6 w-6 animate-spin rounded-full border-2 border-zinc-600 border-t-emerald-400" />
        </div>
      ) : (
        <>
          {/* Metrics Row */}
          <div className="grid grid-cols-4 gap-4 mb-6">
            <MetricCard
              label="Conversion Rate"
              value={`${data.conversionRate}%`}
              icon={TrendingUp}
              color="emerald"
            />
            <MetricCard
              label="Converted"
              value={data.convertedCount.toString()}
              subtext={`of ${data.totalLeads} leads`}
              color="blue"
              onClick={handleConvertedClick}
            />
            <MetricCard
              label="Total Deals"
              value={data.totalDeals.toString()}
              color="purple"
              onClick={handleTotalDealsClick}
            />
            <MetricCard
              label="Avg. Days"
              value={data.avgDaysToConvert.toString()}
              subtext="to convert"
              icon={Clock}
              color="orange"
            />
          </div>

          {/* Conversion by Stage */}
          {Object.keys(data.conversionsByLeadStage).length > 0 && (
            <div className="mb-6">
              <h4 className="text-sm font-medium text-zinc-300 mb-3">Conversions by Lead Stage</h4>
              <div className="flex flex-wrap gap-2">
                {Object.entries(data.conversionsByLeadStage)
                  .sort(([, a], [, b]) => b - a)
                  .map(([stage, count]) => (
                    <span
                      key={stage}
                      className="inline-flex items-center gap-1.5 rounded-full bg-zinc-800 px-3 py-1 text-xs"
                    >
                      <span className="text-zinc-400">{stage}</span>
                      <span className="font-semibold text-emerald-400">{count}</span>
                    </span>
                  ))}
              </div>
            </div>
          )}

          {/* Recent Conversions */}
          {recentConversions.length > 0 && (
            <div>
              <h4 className="text-sm font-medium text-zinc-300 mb-3">Recent Conversions</h4>
              <div className="space-y-2">
                {recentConversions.map((conversion) => (
                  <ConversionRow key={conversion.deal.id} conversion={conversion} />
                ))}
              </div>
            </div>
          )}

          {data.convertedCount === 0 && (
            <div className="text-center py-6 text-zinc-500">
              <p>No lead-to-deal conversions tracked yet.</p>
              <p className="text-xs mt-1">Deals are matched to leads by company name.</p>
            </div>
          )}
        </>
      )}
    </div>
  );
});

interface MetricCardProps {
  label: string;
  value: string;
  subtext?: string;
  icon?: React.ElementType;
  color: 'emerald' | 'blue' | 'purple' | 'orange';
  onClick?: () => void;
}

function MetricCard({ label, value, subtext, icon: Icon, color, onClick }: MetricCardProps) {
  const colorClasses = {
    emerald: 'text-emerald-400',
    blue: 'text-blue-400',
    purple: 'text-purple-400',
    orange: 'text-[#F78C4E]',
  };

  const content = (
    <>
      <div className="flex items-center gap-1.5 mb-1">
        {Icon && <Icon className={`h-3.5 w-3.5 ${colorClasses[color]}`} />}
        <span className="text-xs text-zinc-500">{label}</span>
      </div>
      <p className={`text-xl font-bold ${colorClasses[color]}`}>{value}</p>
      {subtext && <p className="text-xs text-zinc-500">{subtext}</p>}
    </>
  );

  if (onClick) {
    return (
      <button
        onClick={onClick}
        className="rounded-lg bg-zinc-800/50 p-3 text-left hover:bg-zinc-800 transition-colors cursor-pointer"
      >
        {content}
      </button>
    );
  }

  return (
    <div className="rounded-lg bg-zinc-800/50 p-3">
      {content}
    </div>
  );
}

interface ConversionRowProps {
  conversion: ConvertedLead;
}

function ConversionRow({ conversion }: ConversionRowProps) {
  const { lead, deal, daysToConvert } = conversion;

  return (
    <div className="flex items-center gap-3 rounded-lg bg-zinc-800/50 px-3 py-2">
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-white truncate">{lead.company}</p>
        <p className="text-xs text-zinc-500">{lead.contactName}</p>
      </div>
      <div className="flex items-center gap-2 shrink-0">
        <span className="text-xs px-2 py-0.5 rounded bg-zinc-700 text-zinc-300">
          {lead.stage}
        </span>
        <ArrowRight className="h-3 w-3 text-emerald-400" />
        <span className="text-xs px-2 py-0.5 rounded bg-emerald-500/20 text-emerald-400">
          {deal.stage}
        </span>
      </div>
      <div className="shrink-0 text-right">
        <p className="text-xs text-zinc-400">{daysToConvert}d</p>
      </div>
    </div>
  );
}
